import React, { useState, useEffect, useMemo } from "react";
import { Send, ArrowLeft, MessageCircle } from "lucide-react";
import { auth, db } from "../services/firebase";
import { getAvatarUrl } from "../hooks/useAvatarUtils";
import {
  fetchUsersByIds,
  getSharedChatId,
  updateChatLastMessage,
} from "../services/meetlyService";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

import styles from "./Chat.module.css";

/**
 * ChatRoom component handles individual real-time messaging inside an active chat.
 * Separated to prevent hook count mismatch errors during early returns.
 */
function ChatRoom({ activeChat, onBack }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");

  // Effect: Listen for real-time messages inside the active chat room
  useEffect(() => {
    if (!activeChat || !auth.currentUser) return;

    const otherUserId = activeChat.id || activeChat.uid;
    const matchId = getSharedChatId(auth.currentUser.uid, otherUserId);
    if (!matchId) return;

    const messagesRef = collection(db, "chats", matchId, "messages");
    const q = query(messagesRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs = snapshot.docs.map((docItem) => {
          const data = docItem.data();
          const parsedDate = data.timestamp
            ? data.timestamp.toDate()
            : new Date();

          const timeString = parsedDate.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });

          return {
            id: docItem.id,
            ...data,
            timeString,
            timestamp: parsedDate,
          };
        });
        setMessages(msgs);
      },
      (error) => {
        console.error("Error listening to shared chat messages:", error);
      }
    );

    return () => unsubscribe();
  }, [activeChat]);

  // Handler: Send a new message to Firestore
  const handleSendMessage = async () => {
    const otherUserId = activeChat?.id || activeChat?.uid;
    const matchId = getSharedChatId(auth.currentUser?.uid, otherUserId);

    if (!inputText.trim() || !activeChat || !matchId || !auth.currentUser)
      return;

    try {
      const messagesRef = collection(db, "chats", matchId, "messages");
      await addDoc(messagesRef, {
        text: inputText,
        senderId: auth.currentUser.uid,
        senderName: auth.currentUser.displayName || "Me",
        timestamp: serverTimestamp(),
      });

      // Keep the chat doc's preview fields in sync with real activity, so
      // the chat list (here and in Profile.jsx) shows the actual latest
      // message instead of whatever text it was created with.
      await updateChatLastMessage(matchId, inputText);

      setInputText("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <div className={styles.chatRoomContainer}>
      {/* Chat Header */}
      <div className={styles.chatHeader}>
        <button onClick={onBack} className={styles.backButton}>
          <ArrowLeft size={20} />
        </button>
        <div className={styles.chatHeaderAvatar}>
          <img src={getAvatarUrl(activeChat)} alt={activeChat.name} />
        </div>
        <div>
          <h3 className={styles.chatHeaderName}>{activeChat.name || "User"}</h3>
          <p className={styles.chatStatusActive}>Active now</p>
        </div>
      </div>

      {/* Message History Feed */}
      <div className={styles.messageHistory}>
        {messages.length === 0 ? (
          <p className={styles.emptyMessagesText}>
            No messages yet. Say hello! 👋
          </p>
        ) : (
          messages.map((msg) => {
            const isMe = msg.senderId === auth.currentUser?.uid;
            return (
              <div
                key={msg.id}
                className={`${styles.messageRow} ${
                  isMe ? styles.messageRowEnd : styles.messageRowStart
                }`}
              >
                <div
                  className={`${styles.messageBubble} ${
                    isMe ? styles.bubbleMe : styles.bubbleThem
                  }`}
                >
                  <p className={styles.messageText}>{msg.text}</p>
                  <span className={styles.messageTime}>{msg.timeString}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Message Input Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
        className={styles.messageForm}
      >
        <input
          type="text"
          placeholder="Type a message..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          className={styles.messageInput}
        />
        <button type="submit" className={styles.sendButton}>
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}

/**
 * ChatScreen component renders the primary matches list and delegates active chats.
 */
export default function ChatScreen({ matches = [] }) {
  const [activeChat, setActiveChat] = useState(null);
  const [chatSubtitles, setChatSubtitles] = useState({});
  const [firestoreChats, setFirestoreChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);

  // Effect: Listen to active chats directly from Firestore collection "chats"
  useEffect(() => {
    if (!auth.currentUser) return;

    const chatsRef = collection(db, "chats");
    const q = query(
      chatsRef,
      where("participants", "array-contains", auth.currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const chatEntries = snapshot.docs
          .map((change) => {
            const chatData = change.data();
            const participants = chatData.participants || [];
            const otherUserId = participants.find(
              (id) => id !== auth.currentUser.uid
            );
            return { chatDocId: change.id, chatData, otherUserId };
          })
          .filter((entry) => entry.otherUserId);

        const usersMap = await fetchUsersByIds(
          chatEntries.map((entry) => entry.otherUserId)
        );

        const chatList = chatEntries.map(
          ({ chatDocId, chatData, otherUserId }) => {
            const userData = usersMap.get(otherUserId) || {};
            return {
              ...userData,
              id: otherUserId,
              uid: otherUserId,
              chatDocId,
              name: userData.name || userData.displayName || "User",
              lastMessage: chatData.lastMessage || "Say hello! 👋",
              updatedAt: chatData.updatedAt,
            };
          }
        );

        setFirestoreChats(chatList);
      } catch (error) {
        console.error("Error fetching chat participants data:", error);
      } finally {
        setLoadingChats(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Merge traditional matches and Firestore active chats
  const allChatUsers = useMemo(() => {
    const combinedChatsMap = new Map();

    matches.forEach((m) => {
      const id = m.id || m.uid;
      if (id) combinedChatsMap.set(id, m);
    });

    firestoreChats.forEach((c) => {
      const id = c.id || c.uid;
      if (id) {
        if (!combinedChatsMap.has(id)) {
          combinedChatsMap.set(id, c);
        } else {
          const existing = combinedChatsMap.get(id);
          combinedChatsMap.set(id, {
            ...existing,
            lastMessage: c.lastMessage || existing.lastMessage,
          });
        }
      }
    });

    return Array.from(combinedChatsMap.values());
  }, [matches, firestoreChats]);

  // Effect: Listen to the latest message of each chat to update preview subtitles
  useEffect(() => {
    if (!auth.currentUser || allChatUsers.length === 0) return;

    const unsubscribes = allChatUsers.map((userObj) => {
      const otherUserId = userObj.id || userObj.uid;
      const matchId = getSharedChatId(auth.currentUser.uid, otherUserId);
      if (!matchId) return () => {};

      const messagesRef = collection(db, "chats", matchId, "messages");
      const q = query(messagesRef, orderBy("timestamp", "desc"));

      return onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
          const lastDoc = snapshot.docs[0].data();
          const isMe = lastDoc.senderId === auth.currentUser.uid;
          const previewText = `${isMe ? "You: " : ""}${lastDoc.text}`;

          setChatSubtitles((prev) => ({
            ...prev,
            [matchId]: previewText,
          }));
        }
      });
    });

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [allChatUsers]);

  // Sort chats placing recent activity at the top
  const sortedChats = useMemo(() => {
    return [...allChatUsers].sort((a, b) => {
      const idA = getSharedChatId(auth.currentUser?.uid, a.id || a.uid);
      const idB = getSharedChatId(auth.currentUser?.uid, b.id || b.uid);
      return (chatSubtitles[idB] ? 1 : 0) - (chatSubtitles[idA] ? 1 : 0);
    });
  }, [allChatUsers, chatSubtitles]);

  // If a chat is active, render the dedicated ChatRoom component cleanly
  if (activeChat) {
    return (
      <ChatRoom activeChat={activeChat} onBack={() => setActiveChat(null)} />
    );
  }

  // Render: Matches & Chat List View
  return (
    <div className={styles.container}>
      <h2 className={styles.screenTitle}>
        <MessageCircle className={styles.accentIcon} size={22} /> Chats &
        Matches
      </h2>

      {loadingChats ? (
        <p className={styles.emptyStateText}>Loading your conversations...</p>
      ) : sortedChats.length === 0 ? (
        <div className={styles.emptyStateContainer}>
          <p className={styles.emptyStateText}>
            No chats yet. Accept event invitations or connect with attendees to
            start chatting!
          </p>
        </div>
      ) : (
        <div className={styles.matchesList}>
          {sortedChats.map((chatUser) => {
            const otherUserId = chatUser.id || chatUser.uid;
            const matchId = getSharedChatId(auth.currentUser?.uid, otherUserId);
            const subtitleText =
              chatSubtitles[matchId] ||
              chatUser.lastMessage ||
              "Tap to start chatting";

            return (
              <div
                key={otherUserId}
                onClick={() => setActiveChat(chatUser)}
                className={styles.matchCard}
              >
                <div className={styles.matchCardAvatar}>
                  <img src={getAvatarUrl(chatUser)} alt={chatUser.name} />
                </div>
                <div className={styles.matchCardInfo}>
                  <h3 className={styles.matchCardName}>
                    {chatUser.name || "User"}
                  </h3>
                  <p className={styles.matchCardSubtitle}>{subtitleText}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
